import { ClusterProfileSchema } from "utils/schemas";
import { getEntity } from "utils/entities";
import { presentClusterProfileLayers } from "utils/presenters";
import { createSelector } from "reselect";
import { PermissionService } from "services/permissions";

export const getRawClusterProfile = getEntity(
  (state) => state.clusterprofile.details.currentClusterProfileId,
  ClusterProfileSchema,
);

export const getSelectedClusterProfileLayers = createSelector(
  getRawClusterProfile,
  (selectedClusterProfile) => {
    return presentClusterProfileLayers(selectedClusterProfile);
  },
);

export const getDetailPermissions = createSelector(
  getRawClusterProfile,
  (profile) => {
    return new PermissionService(
      profile?.metadata?.annotations?.permissions?.split(","),
    );
  },
);

const getPacksParams = createSelector(
  getRawClusterProfile,
  (clusterprofile) => {
    const clusterProfilePacks = clusterprofile?.spec?.published?.packs;
    const paramsConfig = clusterProfilePacks.reduce((accumulator, value) => {
      const { guid, params, pack } = value;
      if (!pack?.spec?.params) {
        accumulator[guid] = [];
        return accumulator;
      }

      const layerConfig = pack.spec.params
        .filter((item) => item.scope === "cluster")
        .map((param) => {
          return {
            ...param,
            value: params[param.name],
          };
        });

      accumulator[guid] = layerConfig;
      return accumulator;
    }, {});

    return paramsConfig;
  },
);

export const getParamsForLayer = createSelector(
  getPacksParams,
  (state) => state.clusterprofile.details.selectedLayer,
  (packs, selectedLayer) => {
    if (packs && selectedLayer && packs[selectedLayer]) {
      return packs[selectedLayer];
    }

    return [];
  },
);

function isUpdateNotificationActionable(notification) {
  const events = notification.action?.events;
  if (!events) {
    return;
  }

  if (notification?.action.isDone) {
    return;
  }

  return notification;
}

export const getUpdateNotification = createSelector(
  getRawClusterProfile,
  (clusterprofile) => {
    const notifications = clusterprofile?.notifications || [];
    return notifications.filter(isUpdateNotificationActionable);
  },
);

export const isNotificationAcknowledged = createSelector(
  getRawClusterProfile,
  (clusterprofile) => {
    return clusterprofile?.notifications?.some(
      (notification) => notification?.action?.isDone === false,
    );
  },
);

export const canUpdateClusterProfile = createSelector(
  getUpdateNotification,
  isNotificationAcknowledged,
  (notification, isNotificationAcknowledged) => {
    return notification.length > 0 && isNotificationAcknowledged;
  },
);

export const areUpdatesInfoOnly = createSelector(
  getRawClusterProfile,
  (clusterprofile) => {
    return clusterprofile?.notifications?.every(
      (notification) => notification?.action?.isInfo === true,
    );
  },
);

export const showInfoNotification = createSelector(
  getRawClusterProfile,
  (state) => state.clusterprofile.details.scopePackValues,
  (clusterprofile, packValues) => {
    if (packValues.length > 0) {
      return false;
    }

    return clusterprofile?.notifications?.some(
      (notification) => notification?.action?.isInfo,
    );
  },
);

export const getNotificationAck = createSelector(
  (state) => state.clusterprofile.details.scopePackValues,
  areUpdatesInfoOnly,
  (packValues, areUpdatesInfoOnly) => {
    if (areUpdatesInfoOnly) {
      return true;
    }

    return packValues?.every((value) => value.isAck === true);
  },
);
